import { useState, useEffect, useRef, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoMdClose } from "react-icons/io";
import {
    TbDownload, TbSend, TbSendOff, TbReceiptTax, TbClipboardCheck, TbClipboardX,
    TbArrowRight, TbArrowLeft, TbCheck, TbFileText, TbFileSpreadsheet, TbAlertTriangle, TbSettings,
} from "react-icons/tb";
import { useEscKey } from "src/hooks/useEscKey";
import { apiRequest } from "src/services/apiClient";
import { getTagStyle } from "src/helpers/randomColor";
import type { Tag } from "src/types";

// Same six views as the invoice list's tabs (see matchesInvoiceView) — reusing their exact
// labels and definitions so the export's "Offen"/"Überfällig"/etc. mean the same thing as the
// tab of the same name. Previously this filtered on the raw status enum directly, which
// silently excluded overdue invoices from "Offen" and had no equivalent of "Überfällig" at all.
type InvoiceCsvView = "all" | "open" | "overdue" | "unsent" | "paid" | "writtenOff";

const VIEW_OPTIONS: { value: InvoiceCsvView; modifier: string; labelKey: string }[] = [
    { value: "all",        modifier: "grey",   labelKey: "invoices.viewAll"        },
    { value: "open",       modifier: "grey",   labelKey: "invoices.viewOpen"       },
    { value: "overdue",    modifier: "red",    labelKey: "invoices.viewOverdue"    },
    { value: "unsent",     modifier: "blue",    labelKey: "invoices.viewUnsent"    },
    { value: "paid",       modifier: "green",  labelKey: "invoices.viewPaid"       },
    { value: "writtenOff", modifier: "purple", labelKey: "invoices.viewWrittenOff" },
];

const DUNNING_OPTIONS = [
    { value: "0", modifier: "grey",   badge: null, labelKey: "filters.dunningLevels.none"      },
    { value: "1", modifier: "green",  badge: "M1", labelKey: "filters.dunningLevels.friendly"  },
    { value: "2", modifier: "orange", badge: "M2", labelKey: "filters.dunningLevels.demanding" },
    { value: "3", modifier: "red",    badge: "M3", labelKey: "filters.dunningLevels.final"     },
    { value: "4", modifier: "blue",   badge: null, labelKey: "filters.dunningLevels.legal"     },
];

// Mirrors CSV_COLUMN_DEFS in backend/src/controllers/invoiceController.ts — keys must match
// exactly, since the selected subset is sent back verbatim as the `columns` query param.
const COLUMN_OPTIONS: { key: string; labelKey: string }[] = [
    { key: "invoiceNr", labelKey: "invoices.csvExport.colInvoiceNr" },
    { key: "date",      labelKey: "invoices.csvExport.colDate"      },
    { key: "dueDate",   labelKey: "invoices.csvExport.colDueDate"   },
    { key: "patient",   labelKey: "invoices.csvExport.colPatient"   },
    { key: "recipient", labelKey: "invoices.csvExport.colRecipient" },
    { key: "amount",    labelKey: "invoices.csvExport.colAmount"    },
    { key: "paid",      labelKey: "invoices.csvExport.colPaid"      },
    { key: "status",    labelKey: "invoices.csvExport.colStatus"    },
    { key: "dunning",   labelKey: "invoices.csvExport.colDunning"   },
    { key: "tag",       labelKey: "invoices.csvExport.colTag"       },
];

const toDateStr = (d: Date) => d.toISOString().slice(0, 10);
const today     = toDateStr(new Date());

interface Props {
    onClose: () => void;
    /** Admin-only, Plus+ feature — omitted entirely when tags aren't available. */
    tags?: Tag[];
}

type ExportFormat = "csv" | "extf";

export function CsvExportPopup({ onClose, tags }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    useEscKey(onClose, true);

    const [step, setStep] = useState<1 | 2>(1);
    const [format, setFormat] = useState<ExportFormat>("csv");

    // Whether Settings → DATEV has the 4 fields an EXTF export needs — checked once on
    // mount so step 2 can show readiness immediately, not just after a failed export.
    const [datevReady, setDatevReady] = useState<boolean | null>(null);
    useEffect(() => {
        apiRequest<{ datev?: { beraterNr?: string; mandantNr?: string; revenueAccount?: string; debtorAccount?: string } }>("/settings")
            .then((s) => {
                const d = s?.datev;
                setDatevReady(!!(d?.beraterNr && d?.mandantNr && d?.revenueAccount && d?.debtorAccount));
            })
            .catch(() => setDatevReady(false));
    }, []);

    const [dateFrom,  setDateFrom]  = useState("");
    const [dateTo,    setDateTo]    = useState(today);
    const [view,      setView]      = useState<InvoiceCsvView>("all");
    const [amountMin, setAmountMin] = useState("");
    const [sent,                setSent]                = useState<"all" | "sent" | "unsent">("all");
    const [dunning,             setDunning]             = useState<string>("");
    const [vatOnly,             setVatOnly]             = useState(false);
    const [questionnaireFilter, setQuestionnaireFilter] = useState<"" | "submitted" | "not_submitted" | "not_sent">("");
    const [tag,                 setTag]                 = useState("");
    const [count,     setCount]     = useState<number | null>(null);
    const [loading,   setLoading]   = useState(false);

    const [columns, setColumns] = useState<Set<string>>(() => new Set(COLUMN_OPTIONS.map((c) => c.key)));
    const toggleColumn = (key: string) =>
        setColumns((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });

    // Load oldest invoice date once on mount
    useEffect(() => {
        apiRequest<{ oldestDate: string | null }>("/invoices/export-csv-meta")
            .then(({ oldestDate }) => {
                if (oldestDate) setDateFrom(toDateStr(new Date(oldestDate)));
            })
            .catch(() => {});
    }, []);

    // Debounced count update
    const countTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (countTimer.current) clearTimeout(countTimer.current);
        countTimer.current = setTimeout(async () => {
            try {
                const params = new URLSearchParams();
                if (dateFrom)  params.set("dateFrom",  dateFrom);
                if (dateTo)    params.set("dateTo",    dateTo);
                if (view !== "all") params.set("view", view);
                if (amountMin && parseFloat(amountMin) > 0) params.set("amountMin", amountMin);
                if (sent !== "all") params.set("sent", sent);
                if (dunning !== "") params.set("dunning", dunning);
                if (vatOnly) params.set("vatOnly", "true");
                if (questionnaireFilter) params.set("questionnaireFilter", questionnaireFilter);
                if (tag) params.set("tag", tag);
                const { count: c } = await apiRequest<{ count: number }>(`/invoices/export-csv-count?${params.toString()}`);
                setCount(c);
            } catch {
                setCount(null);
            }
        }, 300);
        return () => { if (countTimer.current) clearTimeout(countTimer.current); };
    }, [dateFrom, dateTo, view, amountMin, sent, dunning, vatOnly, questionnaireFilter, tag]);

    const handleDateFromChange = (val: string) => {
        setDateFrom(val);
        if (dateTo && val > dateTo) setDateTo(val);
    };

    const handleDateToChange = (val: string) => {
        if (val > today) return;
        setDateTo(val);
        if (dateFrom && val < dateFrom) setDateFrom(val);
    };

    const handleExport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom)  params.set("dateFrom",  dateFrom);
            if (dateTo)    params.set("dateTo",    dateTo);
            if (view !== "all") params.set("view", view);
            if (amountMin && parseFloat(amountMin) > 0) params.set("amountMin", amountMin);
            if (sent !== "all") params.set("sent", sent);
            if (dunning !== "") params.set("dunning", dunning);
            if (vatOnly) params.set("vatOnly", "true");
            if (questionnaireFilter) params.set("questionnaireFilter", questionnaireFilter);
            if (tag) params.set("tag", tag);

            const isExtf = format === "extf";
            if (!isExtf) {
                const selectedColumns = COLUMN_OPTIONS.map((c) => c.key).filter((k) => columns.has(k));
                params.set("columns", selectedColumns.join(","));
            }

            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const endpoint = isExtf ? "export-extf" : "export-csv";
            const url = `${config.baseUrl}/api/invoices/${endpoint}?${params.toString()}`;
            const res = await fetch(url, {
                headers: {
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                },
            });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const href = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = href;
            const today8 = new Date().toISOString().slice(0, 10);
            a.download = isExtf ? `EXTF_Buchungsstapel_${today8}.csv` : `invoices-${today8}.csv`;
            a.click();
            URL.revokeObjectURL(href);
            onClose();
        } catch {
            // keep popup open on error
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="popup-overlay" onClick={onClose}>
            <div className="popup csv-export-popup" onClick={(e) => e.stopPropagation()}>
                <div className="popup__header">
                    <span className="popup__title">{t("invoices.csvExport.title")}</span>
                    <button className="popup__close" onClick={onClose}><IoMdClose /></button>
                </div>

                <div className="csv-wizard-steps">
                    <div className={`csv-wizard-step${step === 1 ? " csv-wizard-step--active" : " csv-wizard-step--done"}`}>
                        <span className="csv-wizard-step__num">{step === 1 ? "1" : <TbCheck />}</span>
                        <span className="csv-wizard-step__label">{t("invoices.csvExport.step1")}</span>
                    </div>
                    <div className="csv-wizard-step__connector" />
                    <div className={`csv-wizard-step${step === 2 ? " csv-wizard-step--active" : ""}`}>
                        <span className="csv-wizard-step__num">2</span>
                        <span className="csv-wizard-step__label">{t("invoices.csvExport.step2")}</span>
                    </div>
                </div>

                <div className="popup__message popup__message--scroll">
                    {step === 1 && (
                    <div className="csv-export-popup__body">
                        <div className="csv-export-popup__row">
                            <label className="csv-export-popup__label">{t("invoices.csvExport.fromDate")}</label>
                            <input
                                type="date"
                                className="csv-export-popup__input"
                                value={dateFrom}
                                max={dateTo || today}
                                onChange={(e) => handleDateFromChange(e.target.value)}
                            />
                        </div>
                        <div className="csv-export-popup__row">
                            <label className="csv-export-popup__label">{t("invoices.csvExport.toDate")}</label>
                            <input
                                type="date"
                                className="csv-export-popup__input"
                                value={dateTo}
                                min={dateFrom}
                                max={today}
                                onChange={(e) => handleDateToChange(e.target.value)}
                            />
                        </div>
                        <div className="csv-export-popup__row csv-export-popup__row--top">
                            <label className="csv-export-popup__label">{t("invoices.csvExport.status")}</label>
                            <div className="inv-filter__chips">
                                {VIEW_OPTIONS.map((o) => {
                                    const active = view === o.value;
                                    return o.value === "all" ? (
                                        <button
                                            key={o.value}
                                            type="button"
                                            className={`inv-filter__chip${active ? " inv-filter__chip--active" : ""}`}
                                            onClick={() => setView(o.value)}
                                        >
                                            {t(o.labelKey)}
                                        </button>
                                    ) : (
                                        <button
                                            key={o.value}
                                            type="button"
                                            className={`invoice-status invoice-status--${o.modifier} inv-filter__chip-badge${active ? " inv-filter__chip-badge--active" : ""}`}
                                            onClick={() => setView(o.value)}
                                        >
                                            {t(o.labelKey)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="csv-export-popup__row">
                            <label className="csv-export-popup__label">{t("invoices.csvExport.amountMin")}</label>
                            <input
                                type="number"
                                className="csv-export-popup__input csv-export-popup__input--narrow"
                                min="0"
                                step="0.01"
                                placeholder="0"
                                value={amountMin}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === "" || parseFloat(v) >= 0) setAmountMin(v);
                                }}
                            />
                        </div>
                        <div className="csv-export-popup__row csv-export-popup__row--top">
                            <label className="csv-export-popup__label">{t("table.sent")}</label>
                            <div className="inv-filter__chips">
                                <button type="button" className={`inv-filter__chip${sent === "all" ? " inv-filter__chip--active" : ""}`} onClick={() => setSent("all")}>{t("filters.all")}</button>
                                <button type="button" className={`inv-filter__chip inv-filter__chip--green${sent === "sent" ? " inv-filter__chip--active" : ""}`} onClick={() => setSent("sent")}><TbSend />{t("filters.sent")}</button>
                                <button type="button" className={`inv-filter__chip inv-filter__chip--neutral${sent === "unsent" ? " inv-filter__chip--active" : ""}`} onClick={() => setSent("unsent")}><TbSendOff />{t("filters.notSent")}</button>
                            </div>
                        </div>
                        <div className="csv-export-popup__row csv-export-popup__row--top">
                            <label className="csv-export-popup__label">{t("table.dunning")}</label>
                            <div className="inv-filter__chips">
                                <button type="button" className={`inv-filter__chip${dunning === "" ? " inv-filter__chip--active" : ""}`} onClick={() => setDunning("")}>{t("filters.all")}</button>
                                {DUNNING_OPTIONS.map((o) => (
                                    <button
                                        key={o.value}
                                        type="button"
                                        className={`dunning dunning--${o.modifier} inv-filter__chip-badge${dunning === o.value ? " inv-filter__chip-badge--active" : ""}`}
                                        onClick={() => setDunning(dunning === o.value ? "" : o.value)}
                                    >
                                        {o.badge && <span className="dunning__badge">{o.badge}</span>}
                                        {t(o.labelKey)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="csv-export-popup__row csv-export-popup__row--top">
                            <label className="csv-export-popup__label">{t("filters.vat")}</label>
                            <div className="inv-filter__chips">
                                <button type="button" className={`inv-filter__chip inv-filter__chip--blue${vatOnly ? " inv-filter__chip--active" : ""}`} onClick={() => setVatOnly((v) => !v)}><TbReceiptTax />{t("filters.vatIncluded")}</button>
                            </div>
                        </div>
                        <div className="csv-export-popup__row csv-export-popup__row--top">
                            <label className="csv-export-popup__label">{t("filters.questionnaire")}</label>
                            <div className="inv-filter__chips">
                                <button type="button" className={`inv-filter__chip${questionnaireFilter === "" ? " inv-filter__chip--active" : ""}`} onClick={() => setQuestionnaireFilter("")}>{t("filters.all")}</button>
                                <button type="button" className={`inv-filter__chip inv-filter__chip--green${questionnaireFilter === "submitted" ? " inv-filter__chip--active" : ""}`} onClick={() => setQuestionnaireFilter(questionnaireFilter === "submitted" ? "" : "submitted")}><TbClipboardCheck />{t("filters.questionnaireSubmitted")}</button>
                                <button type="button" className={`inv-filter__chip inv-filter__chip--neutral${questionnaireFilter === "not_submitted" ? " inv-filter__chip--active" : ""}`} onClick={() => setQuestionnaireFilter(questionnaireFilter === "not_submitted" ? "" : "not_submitted")}><TbClipboardX />{t("filters.questionnaireNotSubmitted")}</button>
                                <button type="button" className={`inv-filter__chip inv-filter__chip--neutral${questionnaireFilter === "not_sent" ? " inv-filter__chip--active" : ""}`} onClick={() => setQuestionnaireFilter(questionnaireFilter === "not_sent" ? "" : "not_sent")}><TbClipboardX />{t("filters.questionnaireNotSent")}</button>
                            </div>
                        </div>
                        {!!tags?.length && (
                            <div className="csv-export-popup__row csv-export-popup__row--top">
                                <label className="csv-export-popup__label">{t("tags.tag")}</label>
                                <div className="inv-filter__chips">
                                    {tags.map((tg) => (
                                        <button
                                            key={tg.id}
                                            type="button"
                                            className={`inv-filter__chip inv-filter__chip--tag${tag === tg.id ? " inv-filter__chip--active" : ""}`}
                                            style={getTagStyle(tg.name) as CSSProperties}
                                            onClick={() => setTag(tag === tg.id ? "" : tg.id)}
                                        >
                                            {tg.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {step === 2 && (
                    <div className="csv-export-popup__body">
                        <div className="csv-export-format">
                            <button
                                type="button"
                                className={`csv-export-format__btn${format === "csv" ? " csv-export-format__btn--active" : ""}`}
                                onClick={() => setFormat("csv")}
                            >
                                <TbFileText />
                                <span>
                                    <strong>{t("invoices.csvExport.formatCsv")}</strong>
                                    <small>{t("invoices.csvExport.formatCsvDesc")}</small>
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`csv-export-format__btn${format === "extf" ? " csv-export-format__btn--active" : ""}`}
                                onClick={() => setFormat("extf")}
                            >
                                <TbFileSpreadsheet />
                                <span>
                                    <strong>{t("invoices.csvExport.formatExtf")}</strong>
                                    <small>{t("invoices.csvExport.formatExtfDesc")}</small>
                                </span>
                            </button>
                        </div>

                        {format === "csv" ? (
                            <>
                                <p className="csv-export-columns__hint">{t("invoices.csvExport.columnsHint")}</p>
                                <div className="csv-export-columns">
                                    {COLUMN_OPTIONS.map((c) => (
                                        <label key={c.key} className="csv-export-columns__item">
                                            <input
                                                type="checkbox"
                                                checked={columns.has(c.key)}
                                                onChange={() => toggleColumn(c.key)}
                                            />
                                            <span>{t(c.labelKey)}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="csv-export-columns__actions">
                                    <button type="button" className="csv-export-columns__link" onClick={() => setColumns(new Set(COLUMN_OPTIONS.map((c) => c.key)))}>
                                        {t("invoices.csvExport.selectAll")}
                                    </button>
                                    <button type="button" className="csv-export-columns__link" onClick={() => setColumns(new Set())}>
                                        {t("invoices.csvExport.selectNone")}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className={`csv-export-readiness${datevReady ? " csv-export-readiness--ok" : " csv-export-readiness--warn"}`}>
                                {datevReady === null ? (
                                    <p>{t("invoices.csvExport.checkingReadiness")}</p>
                                ) : datevReady ? (
                                    <p><TbCheck /> {t("invoices.csvExport.datevReady")}</p>
                                ) : (
                                    <>
                                        <p><TbAlertTriangle /> {t("invoices.csvExport.datevNotReady")}</p>
                                        <button
                                            type="button"
                                            className="csv-export-readiness__link"
                                            onClick={() => { onClose(); navigate("/settings?section=datev"); }}
                                        >
                                            <TbSettings /> {t("invoices.csvExport.datevGoToSettings")}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    )}
                </div>

                <div className="csv-export-popup__footer">
                    <span className="csv-export-popup__count">
                        {step === 1
                            ? (count === null ? "…" : t("invoices.csvExport.invoiceCount", { count }))
                            : format === "csv"
                                ? t("invoices.csvExport.columnsSelected", { count: columns.size })
                                : t(`invoices.csvExport.formatExtf`)}
                    </span>
                    <div className="popup__actions">
                        {step === 1 ? (
                            <>
                                <button className="popup__btn popup__btn--default" onClick={onClose}>
                                    {t("invoices.csvExport.cancel")}
                                </button>
                                <button
                                    className="popup__btn popup__btn--primary"
                                    onClick={() => setStep(2)}
                                    disabled={count === 0 || count === null}
                                >
                                    {t("invoices.csvExport.next")}
                                    <TbArrowRight />
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="popup__btn popup__btn--default" onClick={() => setStep(1)}>
                                    <TbArrowLeft />
                                    {t("invoices.csvExport.back")}
                                </button>
                                <button
                                    className="popup__btn popup__btn--primary"
                                    onClick={handleExport}
                                    disabled={loading || (format === "csv" ? columns.size === 0 : !datevReady)}
                                >
                                    <TbDownload />
                                    {loading ? "…" : t("invoices.csvExport.export")}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
